import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { DateTime } from 'luxon';
import { NavIcon } from '@/components/icons';
import { LanguageSelector } from './LanguageSelector';
import { browserTimeZone, viewerDiffersFromEstablishment } from '@/features/userManagement/weeklyScheduleTimezone';
import { useAuth } from '@/hooks/useAuth';
import { PAGE_HEADER_PADDING_X } from '@/styles/classes';
import { resolvedLocale, zonedDateAndTime } from '@/utils/timezoneClock';
import { SettingsMenu } from './SettingsMenu';

const headerBtn =
  'inline-flex items-center rounded-md p-2 text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50';

function getGreetingKey(hour: number): 'common.goodMorning' | 'common.goodAfternoon' | 'common.goodEvening' {
  if (hour < 12) {
    return 'common.goodMorning';
  }
  if (hour < 18) {
    return 'common.goodAfternoon';
  }
  return 'common.goodEvening';
}

export const PageHeader: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const [now, setNow] = useState(() => DateTime.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(DateTime.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const lang = i18n.language.split('-')[0];
  const locale = resolvedLocale(i18n.language);
  const establishmentZone = user?.weekly_schedule?.timezone;
  const viewerZone = browserTimeZone();
  const establishmentClock = establishmentZone
    ? zonedDateAndTime(establishmentZone, now, locale)
    : null;
  const currentDate =
    lang === 'ook'
      ? t('common.ook')
      : establishmentClock
        ? establishmentClock.date
        : now.setLocale(locale).toLocaleString({
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          });
  const viewerClock = zonedDateAndTime(viewerZone, now, locale);
  const viewerClockIsSeparate =
    establishmentZone !== undefined &&
    viewerDiffersFromEstablishment(establishmentZone, viewerZone);

  const displayName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.username;

  const greeting = t(
    getGreetingKey(establishmentClock ? establishmentClock.hour : now.hour)
  );

  return (
    <header
      className={clsx(
        'flex flex-wrap items-center justify-between gap-3 bg-blue-navbar py-3 sm:gap-4 sm:py-4',
        PAGE_HEADER_PADDING_X
      )}
    >
      <Link
        to="/"
        className="shrink-0 rounded-sm transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-white/50"
        title={t('nav.dashboard')}
        aria-label={t('nav.dashboard')}
      >
        <img
          src="/images/dashboard/nav-logo.png"
          alt=""
          className="h-8 w-auto max-w-[130px] object-contain object-left sm:h-[38px]"
        />
      </Link>

      <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-4">
        <div className="hidden flex-col items-end text-white md:flex">
          <span className="text-base font-medium leading-6">
            {greeting}, {displayName}
          </span>
          <span className="text-xs leading-[18px]">
            {establishmentClock
              ? `${currentDate} ${establishmentClock.time}`
              : currentDate}
          </span>
          {viewerClockIsSeparate ? (
            <span className="text-xs leading-[18px]">
              {t('common.yourLocalTime', { time: viewerClock.time })}
            </span>
          ) : null}
        </div>

        <LanguageSelector />

        <SettingsMenu />

        <button type="button" className={headerBtn} aria-label={t('common.notifications')}>
          <NavIcon name="notifications" className="size-5" />
        </button>

        <button type="button" className={headerBtn} onClick={() => logout()}>
          <span className="text-sm font-medium">{t('common.logout')}</span>
        </button>
      </div>
    </header>
  );
};
